import React, { useState, useRef, useEffect } from 'react';
import MapComponent from '../../../components/MapComponent';
import { styles } from './dashboardStyles';
import { theme } from '../../../theme';
import Tooltip from '../../../components/Tooltip';
import Toast from '../../../components/Toast';
import ActionVariationsPicker from '../../../components/ActionVariationsPicker';

const getEventStatusText = (ev) => {
    if (ev.closureType === 'manual_early') return 'Cancelled by admin.';
    const isClosed = ev.status === 'closed' || new Date(ev.endDate) < new Date();
    const dateStr = new Date(ev.endDate).toLocaleDateString('en-GB');
    return isClosed ? 'Closed' : 'Open until ' + dateStr;
};

const getEventSelectColor = (ev) => {
    if (ev.closureType === 'manual_early') return '#94A3B8';
    const isClosed = ev.status === 'closed' || new Date(ev.endDate) < new Date();
    return isClosed ? '#94A3B8' : '#0F172A';
};

// When exactly one allowed action of the relevant type (Field vs Whole Farm)
// exists for the event, pre-select it rather than leaving the dropdown blank.
const getSoleActionId = (eventDetails, isWholeFarm) => {
    if (!eventDetails) return '';
    const matches = eventDetails.allowedActions.filter(a => isWholeFarm ? a.isWholeFarm : !a.isWholeFarm);
    return matches.length === 1 ? matches[0].actionId : '';
};

// Custom dropdown (native <option> can't bold just the event name — browsers
// render the open list via an OS-native popup that ignores font-weight there).
const EventSelect = ({ events, selectedEvent, onChange }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [hoveredId, setHoveredId] = useState(null);
    const wrapperRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setIsOpen(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const currentEvent = events.find(ev => ev._id === selectedEvent);
    const getEventLabel = (ev) => ev.clientId?.name ? `${ev.name} - ${ev.clientId.name}` : ev.name;

    return (
        <div ref={wrapperRef} style={{ position: 'relative', marginBottom: '10px' }}>
            <button
                type="button"
                onClick={() => setIsOpen(prev => !prev)}
                style={{ ...styles.select, marginBottom: 0, textAlign: 'left', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
            >
                {currentEvent ? (
                    <span style={{ color: getEventSelectColor(currentEvent) }}>
                        <strong>{getEventLabel(currentEvent)}</strong> {getEventStatusText(currentEvent)}
                    </span>
                ) : <span>-- Select Event --</span>}
                <span style={{ marginLeft: '10px', color: '#475569' }}>{isOpen ? '▲' : '▼'}</span>
            </button>

            {isOpen && (
                <ul style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 20, backgroundColor: '#FFFFFF', border: '1px solid #ddd', borderRadius: '4px', marginTop: '4px', maxHeight: '260px', overflowY: 'auto', listStyle: 'none', padding: '4px 0', margin: '4px 0 0 0', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}>
                    {events.map(ev => {
                        return (
                            <li
                                key={ev._id}
                                onClick={() => { onChange(ev._id); setIsOpen(false); }}
                                onMouseEnter={() => setHoveredId(ev._id)}
                                onMouseLeave={() => setHoveredId(null)}
                                style={{
                                    padding: '8px 12px',
                                    cursor: 'pointer',
                                    color: getEventSelectColor(ev),
                                    backgroundColor: hoveredId === ev._id ? '#F1F5F9' : (ev._id === selectedEvent ? '#F8FAFC' : '#FFFFFF'),
                                    fontFamily: '"Inter", sans-serif',
                                    fontSize: '0.9rem'
                                }}
                            >
                                <strong>{getEventLabel(ev)}</strong> {getEventStatusText(ev)}
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
};

const OddsBadge = ({ bidId, getOddsInfo }) => {
    const odds = getOddsInfo(bidId);
    return (
        <div>
            <span style={{ ...styles.oddsBadge, backgroundColor: odds.bg, borderColor: odds.border, color: odds.color }}>
                {odds.label}
            </span>
            {odds.checkedAtLabel && <div style={styles.oddsCheckedAt}>{odds.checkedAtLabel}</div>}
        </div>
    );
};

// Uploaded attachment `url`s are stored as relative paths (e.g. /uploads/bids/xyz.pdf)
// — resolve them against the API's origin (stripping the trailing /api) so they're
// directly clickable from the browser.
const UPLOADS_BASE_URL = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace(/\/api\/?$/, '');

// Whole Farm / Manual Review proposal form, opened as a modal (rather than inline in
// the grid like the spatial bid form) since there's no map/parcel context driving it.
const ProposalModal = ({
    activeEventDetails, isAuctionActive,
    selectedAction, setSelectedAction,
    proposalForm, setProposalForm,
    existingAttachments, setExistingAttachments,
    newAttachmentFiles, setNewAttachmentFiles,
    onClose, onSave
}) => {
    const handleFieldChange = (field, value) => setProposalForm(prev => ({ ...prev, [field]: value }));

    const handleFilePick = (e) => {
        const picked = Array.from(e.target.files || []);
        setNewAttachmentFiles(prev => [...prev, ...picked]);
        e.target.value = '';
    };

    const removeExisting = (id) => setExistingAttachments(prev => prev.filter(a => a._id !== id));
    const removeNewFile = (index) => setNewAttachmentFiles(prev => prev.filter((_, i) => i !== index));

    // Admin-configurable per-action prompts (Event Setup) — fall back to the
    // original hardcoded labels for older events saved before this field existed.
    const currentActionDef = activeEventDetails?.allowedActions?.find(a => a.actionId === selectedAction);
    const DEFAULT_QUESTIONS = ['Proposal Overview', 'Expected Environmental Impact', 'Evaluation & Verification Plan'];
    const questions = (currentActionDef?.questions?.length === 3) ? currentActionDef.questions : DEFAULT_QUESTIONS;
    const fundingOptional = !!currentActionDef?.fundingOptional;

    return (
        <div style={styles.modalOverlay}>
            <div style={{ ...styles.modalBox, maxWidth: '700px', maxHeight: '85vh', overflowY: 'auto' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h3 style={{ marginTop: 0, ...styles.h2 }}>Whole Farm Proposal</h3>
                    <button type="button" onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.2rem', cursor: 'pointer', color: '#475569' }}>✕</button>
                </div>

                {isAuctionActive ? (
                    <form onSubmit={onSave}>
                        <label style={styles.label}>Action</label>
                        <select style={styles.select} value={selectedAction} onChange={(e) => setSelectedAction(e.target.value)} required>
                            <option value="">-- Choose Action --</option>
                            {activeEventDetails?.allowedActions.map(a => (
                                <option key={a.actionId} value={a.actionId}>{a.label}</option>
                            ))}
                        </select>

                        <label style={styles.label}>Value of grant requested{fundingOptional ? ' (optional)' : ''}</label>
                        <input
                            type="number" min="0" placeholder="e.g. 25000"
                            value={proposalForm.bidPrice}
                            onChange={(e) => handleFieldChange('bidPrice', e.target.value)}
                            style={{ ...styles.input, marginBottom: '15px' }}
                            required={!fundingOptional}
                        />

                        <label style={styles.label}>{questions[0]}</label>
                        <textarea
                            placeholder="Summarise your proposal..."
                            value={proposalForm.proposalOverview}
                            onChange={(e) => handleFieldChange('proposalOverview', e.target.value)}
                            style={{ ...styles.input, height: '70px', resize: 'vertical' }}
                        />

                        <label style={styles.label}>{questions[1]}</label>
                        <textarea
                            placeholder="Describe the expected environmental outcomes..."
                            value={proposalForm.impactStatement}
                            onChange={(e) => handleFieldChange('impactStatement', e.target.value)}
                            style={{ ...styles.input, height: '70px', resize: 'vertical' }}
                        />

                        <label style={styles.label}>{questions[2]}</label>
                        <textarea
                            placeholder="How will delivery be evaluated and verified?"
                            value={proposalForm.evaluationStatement}
                            onChange={(e) => handleFieldChange('evaluationStatement', e.target.value)}
                            style={{ ...styles.input, height: '70px', resize: 'vertical' }}
                        />

                        <label style={styles.label}>Attachments</label>
                        <div style={{ border: '1px dashed #CBD5E1', borderRadius: '6px', padding: '15px', marginBottom: '15px', backgroundColor: '#F8FAFC' }}>
                            <label style={{ ...styles.btnReversed, display: 'inline-block', cursor: 'pointer', margin: 0 }}>
                                + Add Files
                                <input type="file" multiple accept=".pdf,.png,.jpg,.jpeg,.docx" onChange={handleFilePick} style={{ display: 'none' }} />
                            </label>
                            <div style={{ fontSize: '0.75rem', color: '#475569', marginTop: '6px' }}>PDF, PNG, JPG, JPEG or DOCX — up to 10MB each.</div>

                            {(existingAttachments.length > 0 || newAttachmentFiles.length > 0) && (
                                <ul style={{ listStyle: 'none', padding: 0, margin: '10px 0 0 0' }}>
                                    {existingAttachments.map(a => (
                                        <li key={a._id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid #eee', fontSize: '0.85rem' }}>
                                            <a href={`${UPLOADS_BASE_URL}${a.url}`} target="_blank" rel="noopener noreferrer" style={{ color: '#0D9488' }}>{a.filename}</a>
                                            <button type="button" onClick={() => removeExisting(a._id)} style={{ background: 'none', border: 'none', color: '#F59E0B', cursor: 'pointer', fontWeight: 'bold', fontSize: '0.8rem' }}>Remove</button>
                                        </li>
                                    ))}
                                    {newAttachmentFiles.map((f, i) => (
                                        <li key={`${f.name}-${i}`} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid #eee', fontSize: '0.85rem' }}>
                                            <span style={{ color: '#0F172A' }}>{f.name} <em style={{ color: '#475569' }}>(pending upload)</em></span>
                                            <button type="button" onClick={() => removeNewFile(i)} style={{ background: 'none', border: 'none', color: '#F59E0B', cursor: 'pointer', fontWeight: 'bold', fontSize: '0.8rem' }}>Remove</button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>

                        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                            <button type="button" onClick={onClose} style={styles.btnReversed}>Cancel</button>
                            <button type="submit" style={styles.btnPrimary}>Save Draft</button>
                        </div>
                    </form>
                ) : (
                    <div style={styles.alertError}><strong>Bidding Closed</strong></div>
                )}
            </div>
        </div>
    );
};

const AuctionBiddingView = ({
    activeSbi, events, selectedEvent, setSelectedEvent, activeEventDetails, actionDescriptions, actionVariations,
    selectedParcel, setSelectedParcel, isWholeFarmBidMode, setIsWholeFarmBidMode,
    myBids, focusAreaZones, bidsLoading,
    isRenaming, setIsRenaming, renameInput, setRenameInput,
    selectedAction, setSelectedAction, bidAmount, setBidAmount, bidUnits, setBidUnits,
    selectedVariations, setSelectedVariations,
    totalActionCost, setTotalActionCost, priceEvidenceFile, setPriceEvidenceFile,
    acceptedTerms, setAcceptedTerms, setShowTermsModal,
    recenterTrigger, user, setSimEventData, setSimModalOpen,
    handleParcelSelect, handleRenameSubmit, handleSaveDraft, handleEditBid, handleDeleteBid,
    handleWithdrawBid, handleCommitBids, onTabRequest,
    getActionLabel, getOddsInfo,
    isProposalModalOpen, setIsProposalModalOpen,
    proposalForm, setProposalForm,
    existingAttachments, setExistingAttachments,
    newAttachmentFiles, setNewAttachmentFiles,
    handleOpenNewProposal, handleSaveProposalDraft,
    proposalSavedBadgeVisible, onDismissProposalSavedBadge
}) => {
    const [showBatchModal, setShowBatchModal] = useState(false);
    const [ratePerHa, setRatePerHa] = useState('');

    const visibleBids = myBids.filter(b => {
        const bidSbi = b.sbi || b.parcel?.sbi;
        return bidSbi && activeSbi && String(bidSbi) === String(activeSbi);
    });
    const drafts = visibleBids.filter(b => b.status === 'draft');
    const submitted = visibleBids.filter(b => b.status !== 'draft');
    const isAuctionActive = activeEventDetails && activeEventDetails.status !== 'closed' && new Date(activeEventDetails.endDate) > new Date();
    const isManualAuction = activeEventDetails?.auctionType === 'manual';
    const isManuallyCancelledEvent = activeEventDetails?.closureType === 'manual_early';

    const isGrantScheme = activeEventDetails?.auctionType === 'grants' || activeEventDetails?.auctionType === 'grants_fixed';
    const isGrantsContribution = activeEventDetails?.auctionType === 'grants_contribution';
    const currentActionDef = activeEventDetails?.allowedActions.find(a => a.actionId === selectedAction);
    const existingBid = selectedParcel ? myBids.find(b => b.parcel?._id === selectedParcel.id) : null;
    const currentActionVariations = actionVariations?.[selectedAction] || [];

    const handleActionChange = (actionId) => {
        setSelectedAction(actionId);
        setSelectedVariations({});
    };

    const handleVariationChange = (variationName, optionName) => {
        setSelectedVariations(prev => ({ ...prev, [variationName]: optionName }));
    };

    // Fixed-Rate Grants: a chosen variation option can carry an extra £/unit
    // on top of the action's base grantPrice (Event.allowedActions[].
    // optionGrantPayments, keyed "variationName|optionName") — reflect that
    // in the displayed rate immediately as the farmer picks options.
    const getAdjustedGrantRate = () => {
        const baseRate = currentActionDef?.grantPrice || 0;
        if (!currentActionDef?.optionGrantPayments) return baseRate;
        return Object.entries(selectedVariations).reduce((rate, [variationName, optionName]) => {
            if (!optionName) return rate;
            const extra = currentActionDef.optionGrantPayments[`${variationName}|${optionName}`];
            return typeof extra === 'number' ? rate + extra : rate;
        }, baseRate);
    };

    // £/ha rate entry is only meaningful when the action's quantity basis IS the
    // parcel's own area (per-parcel 'ha' actions) — whole-farm 'ha' actions take
    // their quantity from the separate Quantity(ha) input instead.
    const showRatePerHaField = !isGrantScheme && !isWholeFarmBidMode && currentActionDef?.unit === 'ha' && !!selectedParcel?.area;

    // Keeps the Rate field showing the rate implied by the current Total whenever
    // the bid context changes underneath it (new parcel/action selected, or an
    // existing bid loaded via Edit) — but not on every keystroke in the Rate
    // field itself, which would fight the user's typing as amounts get rounded.
    useEffect(() => {
        if (showRatePerHaField && bidAmount) {
            const area = parseFloat(selectedParcel.area);
            if (area > 0) {
                setRatePerHa((parseFloat(bidAmount) / area).toFixed(2));
                return;
            }
        }
        setRatePerHa('');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedParcel?.id, selectedAction, isWholeFarmBidMode]);

    const handleRatePerHaChange = (e) => {
        const val = e.target.value;
        setRatePerHa(val);
        const area = parseFloat(selectedParcel?.area);
        if (area > 0 && val !== '' && !isNaN(val)) {
            setBidAmount(String(Math.round(parseFloat(val) * area)));
        } else if (val === '') {
            setBidAmount('');
        }
    };

    const handleTotalPriceBlur = () => {
        if (!showRatePerHaField || !bidAmount) return;
        const area = parseFloat(selectedParcel.area);
        if (area > 0) setRatePerHa((parseFloat(bidAmount) / area).toFixed(2));
    };

    const getActionUnit = (actionId) => activeEventDetails?.allowedActions.find(a => a.actionId === actionId)?.unit;

    // Buckets bids by action, with a quantity subtotal (parcel area for 'ha'
    // actions, else summed units) and a cost subtotal per group.
    const groupBidsByAction = (bids) => {
        const groups = {};
        bids.forEach(bid => {
            const key = bid.selectedAction;
            if (!groups[key]) {
                groups[key] = { actionId: key, label: getActionLabel(key), unit: getActionUnit(key), bids: [], quantityTotal: 0, costTotal: 0 };
            }
            const g = groups[key];
            g.bids.push(bid);
            g.costTotal += bid.amount || 0;
            if (g.unit === 'ha') {
                if (bid.parcel && bid.parcel.area) g.quantityTotal += parseFloat(bid.parcel.area) || 0;
            } else {
                g.quantityTotal += bid.units || 0;
            }
        });
        return Object.values(groups).sort((a, b) => a.label.localeCompare(b.label));
    };

    const formatGroupQuantity = (group) => {
        if (group.unit === 'ha') return `${group.quantityTotal.toFixed(2)} ha total`;
        if (group.unit) return `${group.quantityTotal} ${group.unit} total`;
        return null;
    };

    const formatGroupCost = (group) => {
        const amount = isGrantScheme ? Math.round(group.costTotal) : group.costTotal;
        return `£${amount.toLocaleString('en-GB')}`;
    };

    // Per-bid rate (e.g. "£653.1/Ha") shown in the Submitted Bids list in place
    // of the (otherwise-tautological, everything here is already submitted)
    // bid status label.
    const formatBidUnitPrice = (bid, group) => {
        if (!group.unit || group.unit === 'farm') return null;
        const quantity = group.unit === 'ha'
            ? parseFloat(bid.parcel?.area) || 0
            : bid.units || 0;
        if (!quantity) return null;
        const unitLabel = group.unit === 'ha' ? 'Ha' : group.unit;
        return `£${(bid.amount / quantity).toFixed(1)}/${unitLabel}`;
    };

    const draftGroups = groupBidsByAction(drafts);
    const submittedGroups = groupBidsByAction(submitted);
    const draftsGrandTotal = drafts.reduce((sum, b) => sum + (b.amount || 0), 0);

    const getUnitPriceDisplay = () => {
        if (!bidAmount || isNaN(bidAmount) || parseFloat(bidAmount) <= 0) return null;
        if (!currentActionDef || currentActionDef.unit === 'farm') return null;

        const amount = parseFloat(bidAmount);
        let quantity = 0;
        let unitLabel = currentActionDef.unit;

        if (unitLabel === 'ha') {
            if (selectedParcel && selectedParcel.area) quantity = parseFloat(selectedParcel.area);
        } else {
            if (bidUnits && !isNaN(bidUnits)) quantity = parseFloat(bidUnits);
        }

        if (quantity > 0) {
            const pricePerUnit = (amount / quantity).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
            return `Equivalent to £${pricePerUnit} per ${unitLabel}`;
        }
        return null;
    };

    const renderAvailableActions = () => {
        if (!activeEventDetails || !activeEventDetails.allowedActions) return null;

        return (
            <div style={{ marginBottom: '10px', backgroundColor: '#F8FAFC', padding: '8px 10px', borderRadius: '6px', border: '1px solid #eee', display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', gap: '4px' }}>
                <h4 style={{ margin: '0 6px 0 0', fontFamily: '"Plus Jakarta Sans", sans-serif', color: '#0F172A', fontSize: '0.9rem', whiteSpace: 'nowrap' }}>Available Actions:</h4>
                {activeEventDetails.allowedActions.map((action, i) => (
                    <span key={action.actionId} style={{ fontSize: '0.8rem', fontFamily: '"Inter", sans-serif', whiteSpace: 'nowrap' }}>
                        {i > 0 && <span style={{ color: '#94A3B8', marginRight: '6px' }}>•</span>}
                        <Tooltip text={actionDescriptions?.[action.actionId] || ''}>
                            <span style={{ fontWeight: 'bold', color: action.isWholeFarm ? theme.colors.accent : '#0D9488' }}>{action.label}</span>
                        </Tooltip>
                    </span>
                ))}
            </div>
        );
    };

    if (!activeSbi) {
        return (
            <div style={{ ...styles.card, maxWidth: '600px', margin: '60px auto', textAlign: 'center' }}>
                <h2 style={styles.h2}>Set Up Your Farm First</h2>
                <p style={styles.textMuted}>Import your fields or draw a custom boundary in My Fields before you can place bids.</p>
                <button onClick={() => onTabRequest('fields')} style={styles.btnPrimary}>Go to My Fields</button>
            </div>
        );
    }

    // Draft/Submitted bid columns — shared between the non-manual "nothing selected"
    // view and the always-shown Manual Review layout (which has no map/parcel
    // selection to toggle away from). Kept as two separate consts (rather than one
    // fragment) so the non-manual grid can place each in its own explicit column.
    const draftBidsCard = (
            <div style={{...styles.listCard, opacity: isManuallyCancelledEvent ? 0.5 : 1}}>
                <div style={styles.listCardHeader}>
                    <Toast
                        message="Proposal Saved - Review and submit to finalise"
                        visible={proposalSavedBadgeVisible}
                        onDismiss={onDismissProposalSavedBadge}
                    />
                    <h3 style={{ marginTop:0, ...styles.h3, color: '#0D9488', borderBottom: '2px solid #0D9488', paddingBottom: '5px' }}>Draft Bids</h3>
                    {isManuallyCancelledEvent && <p style={{margin: '4px 0 0 0', fontSize: '0.8rem', color: '#94A3B8'}}>Event cancelled — no action needed.</p>}
                </div>
                <div style={styles.listCardBody}>
                    {!bidsLoading && drafts.length === 0 ? <p style={{color:'#475569', fontSize: '0.9rem'}}>{isManualAuction ? 'No drafts yet. Click "+ Add Proposal" to start.' : 'No drafts yet. Select a field on the map to start.'}</p> : (
                        draftGroups.map(group => (
                            <div key={group.actionId} style={{ marginBottom: '10px' }}>
                                <div style={styles.groupHeader}>
                                    <span style={styles.groupHeaderLabel}>{group.label}</span>
                                    <span style={styles.groupSubtotal}>
                                        {formatGroupQuantity(group) ? `${formatGroupQuantity(group)} · ` : ''}{formatGroupCost(group)}
                                    </span>
                                </div>
                                <ul style={styles.bidList}>
                                    {group.bids.map(bid => {
                                        const displayPrice = isGrantScheme ? Math.round(bid.amount).toLocaleString('en-GB') : bid.amount.toLocaleString('en-GB');

                                        return (
                                            <li key={bid._id} style={{...styles.bidItem, padding: '15px', backgroundColor: '#F8FAFC', borderRadius: '6px', marginBottom: '10px', border: '1px solid #eee', flexDirection: 'column', alignItems: 'stretch' }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                                    <div style={{flex:1}}>
                                                        <div style={{ fontSize: '12px', fontWeight: 'bold', fontFamily: '"Inter", sans-serif' }}>
                                                            <span style={{ color: '#0D9488' }}>{bid.parcel ? bid.parcel.parcelRef : "Whole Farm Action"}</span>
                                                            {bid.parcel && bid.parcel.customName && (
                                                                <span style={{ color: '#0F172A', marginLeft: '6px' }}>— {bid.parcel.customName}</span>
                                                            )}
                                                        </div>
                                                        <div style={{fontSize:'0.85rem', color:'#475569', marginTop:'4px'}}>
                                                            {isGrantScheme ? 'Grant Value: ' : ''}£{displayPrice} {bid.units > 0 && bid.parcel && currentActionDef?.unit !== 'ha' ? `(Qty: ${bid.units})` : ''}
                                                        </div>
                                                    </div>
                                                    {isAuctionActive && (
                                                        <div style={{display:'flex', flexDirection: 'column', gap:'5px'}}>
                                                            <button onClick={() => handleEditBid(bid)} style={{...styles.btnSecondary, fontSize: '0.75rem', padding: '6px'}}>Edit</button>
                                                            <button onClick={() => handleDeleteBid(bid._id)} style={{...styles.btnDangerOutline, fontSize: '0.75rem', padding: '6px'}}>Delete</button>
                                                        </div>
                                                    )}
                                                </div>
                                            </li>
                                        );
                                    })}
                                </ul>
                            </div>
                        ))
                    )}
                </div>
                {drafts.length > 0 && isAuctionActive && (
                    <div style={styles.listCardFooter}>
                        <button
                            onClick={() => setShowBatchModal(true)}
                            style={{ ...styles.btnPrimary, width: '100%', padding: '12px' }}
                        >
                            Review & Submit {drafts.length} Draft Bid{drafts.length !== 1 ? 's' : ''}
                        </button>
                    </div>
                )}
            </div>
    );

    const submittedBidsCard = (
            <div style={{...styles.listCard, opacity: isManuallyCancelledEvent ? 0.5 : 1}}>
                <div style={styles.listCardHeader}>
                    <h3 style={{ marginTop:0, ...styles.h3, color: '#F59E0B', borderBottom: '2px solid #F59E0B', paddingBottom: '5px' }}>Submitted Bids</h3>
                    {isManuallyCancelledEvent && <p style={{margin: '4px 0 0 0', fontSize: '0.8rem', color: '#94A3B8'}}>Event cancelled — no action needed.</p>}
                    {isAuctionActive && (
                        <button
                            onClick={() => {
                                setSimEventData({ id: selectedEvent, name: activeEventDetails?.name });
                                setSimModalOpen(true);
                            }}
                            style={{ ...styles.btnPrimary, width: '100%', padding: '8px', fontSize: '0.85rem', marginBottom: '10px' }}
                        >
                            Check Odds of Winning
                        </button>
                    )}
                </div>
                <div style={styles.listCardBody}>
                    {submitted.length === 0 ? <p style={{color:'#475569', fontSize: '0.9rem'}}>No submitted bids for this event.</p> : (
                        submittedGroups.map(group => (
                            <div key={group.actionId} style={{ marginBottom: '10px' }}>
                                <div style={styles.groupHeader}>
                                    <span style={styles.groupHeaderLabel}>{group.label}</span>
                                    <span style={styles.groupSubtotal}>
                                        {formatGroupQuantity(group) ? `${formatGroupQuantity(group)} · ` : ''}{formatGroupCost(group)}
                                    </span>
                                </div>
                                <ul style={{listStyle:'none', padding:0, margin: 0}}>
                                    {group.bids.map(bid => {
                                        const displayPrice = isGrantScheme ? Math.round(bid.amount).toLocaleString('en-GB') : bid.amount.toLocaleString('en-GB');
                                        const unitPrice = formatBidUnitPrice(bid, group);
                                        return (
                                            <li key={bid._id} style={{ padding:'12px', borderBottom:'1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                                                <div>
                                                    <div style={{ fontSize: '12px', fontWeight: 'bold', fontFamily: '"Inter", sans-serif' }}>
                                                        <span style={{ color: '#0F172A' }}>{bid.parcel ? bid.parcel.parcelRef : "Whole Farm Action"}</span>
                                                        {bid.parcel && bid.parcel.customName && (
                                                            <span style={{ color: '#475569', marginLeft: '6px', fontWeight: 'normal' }}>— {bid.parcel.customName}</span>
                                                        )}
                                                    </div>
                                                    <div style={{fontSize:'0.8rem', color:'#475569', marginTop: '2px'}}>£{displayPrice}</div>
                                                </div>
                                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '5px' }}>
                                                    {unitPrice && (
                                                        <span style={{ fontSize:'0.75rem', fontWeight:'bold', fontFamily: '"Inter", sans-serif', color: '#0F172A' }}>
                                                            {unitPrice}
                                                        </span>
                                                    )}
                                                    <OddsBadge bidId={bid._id} getOddsInfo={getOddsInfo} />
                                                    {bid.status === 'pending' && isAuctionActive && (
                                                        <div style={{ display: 'flex', gap: '5px' }}>
                                                            <button onClick={() => handleEditBid(bid)} style={{...styles.btnSecondary, padding: '4px 8px', fontSize: '11px'}}>Revise</button>
                                                            <button onClick={() => handleWithdrawBid(bid)} style={styles.btnDangerSmall}>Withdraw</button>
                                                        </div>
                                                    )}
                                                </div>
                                            </li>
                                        );
                                    })}
                                </ul>
                            </div>
                        ))
                    )}
                </div>
            </div>
    );

    // Combined fragment for the Manual Review layout's simple 2-column grid,
    // where both cards auto-place into their own column with no explicit
    // positioning needed (unlike the non-manual grid below).
    const bidLists = (<>{draftBidsCard}{submittedBidsCard}</>);

    const selectEventBlock = (
        <div style={{ ...styles.card, marginBottom: 0 }}>
            <h3 style={{ marginTop: 0, ...styles.hHighlight }}>Select Event</h3>
            <EventSelect events={events} selectedEvent={selectedEvent} onChange={setSelectedEvent} />

            {renderAvailableActions()}
        </div>
    );

    return (
    <>
    <div style={styles.dashboardPage}>
    {isManualAuction ? (
    <>
        {selectEventBlock}
        <div style={{ ...styles.dashboardGrid, gridTemplateColumns: '1fr 1fr' }}>
            <div style={{ gridColumn: '1 / span 2' }}>
                <button
                    onClick={handleOpenNewProposal}
                    disabled={!isAuctionActive}
                    style={{ ...styles.btnPrimary, width: '100%', padding: '15px', opacity: isAuctionActive ? 1 : 0.5, cursor: isAuctionActive ? 'pointer' : 'not-allowed' }}
                >
                    + Add Proposal
                </button>
            </div>
            {bidLists}
        </div>
    </>
    ) : (
    <div style={{ ...styles.dashboardGrid, gridTemplateColumns: '1fr 1fr 2fr', gridTemplateRows: 'auto 1fr' }}>
        <div style={{ gridColumn: '1 / span 2', gridRow: '1' }}>
            {selectEventBlock}
        </div>

        <div style={{ ...styles.mapCard, gridColumn: '3', gridRow: '1 / span 2', height: '100%', marginBottom: 0 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px', marginBottom: '10px' }}>
                <h2 style={{ margin: 0, ...styles.h2 }}>Farm Map</h2>
                <div style={{ fontSize: '0.85rem', color: '#0D9488', fontWeight: 'bold', fontFamily: '"Inter", sans-serif', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span>Click on a field to update its bidding, or</span>
                    <button
                        type="button"
                        onClick={() => {
                            setIsWholeFarmBidMode(true);
                            setSelectedParcel(null);
                            setSelectedAction(getSoleActionId(activeEventDetails, true));
                            setBidAmount('');
                            setBidUnits('');
                            setSelectedVariations({});
                        }}
                        style={{
                            backgroundColor: theme.colors.accent, color: '#FFFFFF', border: 'none', borderRadius: '12px',
                            padding: '5px 12px', cursor: 'pointer', fontWeight: 'bold', fontSize: '0.8rem', fontFamily: '"Inter", sans-serif'
                        }}
                    >
                        Add Whole Farm Bid
                    </button>
                </div>
            </div>
            <div style={styles.mapContainer}>
                <MapComponent
                    key={`${activeSbi}-${recenterTrigger}`}
                    onParcelSelect={handleParcelSelect}
                    filterSbi={activeSbi}
                    bids={visibleBids}
                    focusAreaZones={focusAreaZones}
                    selectedParcelId={selectedParcel?.id || null}
                    homePostcode={user?.postcode}
                  />
            </div>
        </div>

        {(selectedParcel || isWholeFarmBidMode) ? (
            <div style={{ ...styles.card, gridColumn: '1 / span 2', gridRow: '2', height: '100%', minHeight: 0, overflowY: 'auto', marginBottom: 0 }}>
                    <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', borderBottom: '1px solid #eee', paddingBottom: '15px', marginBottom: '15px' }}>
                            <div>
                                {isWholeFarmBidMode ? (
                                    <>
                                        <h2 style={{ margin: 0, ...styles.h2, fontSize: '1.5rem' }}>Whole Farm Action</h2>
                                        <h4 style={{ margin: '5px 0 0 0', color: '#475569', fontWeight: 'normal', fontSize: '0.9rem' }}>Applies to entire holding</h4>
                                    </>
                                ) : (
                                    <>
                                        <h2 style={{ margin: '0 0 5px 0', ...styles.h2, fontSize: '20px', color: '#0D9488' }}>
                                            <span style={{ fontSize: '14px', color: '#475569', fontWeight: 'normal' }}>RPA ID: </span>
                                            {selectedParcel.parcelRef}
                                        </h2>

                                        {isRenaming ? (
                                            <form onSubmit={handleRenameSubmit} style={{ display: 'flex', gap: '8px', margin: '8px 0', alignItems: 'center' }}>
                                                <span style={{ color: '#475569', fontSize: '14px' }}>Custom Name:</span>
                                                <input
                                                    type="text"
                                                    value={renameInput}
                                                    onChange={(e) => setRenameInput(e.target.value)}
                                                    placeholder="Enter a custom name..."
                                                    autoFocus
                                                    style={{ ...styles.input, padding: '6px 8px', marginBottom: 0, flex: 1 }}
                                                />
                                                <button type="submit" style={{ ...styles.btnPrimary, padding: '6px 12px', fontSize: '0.8rem' }}>Save</button>
                                                <button type="button" onClick={() => setIsRenaming(false)} style={{ ...styles.btnReversed, padding: '6px 12px', fontSize: '0.8rem' }}>Cancel</button>
                                            </form>
                                        ) : (
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', margin: '5px 0 10px 0' }}>
                                                <h4 style={{ margin: 0, color: '#0F172A', fontWeight: 'bold', fontSize: '20px', fontFamily: '"Plus Jakarta Sans", sans-serif' }}>
                                                    <span style={{ fontWeight: 'normal', color: '#475569', fontSize: '14px' }}>Custom Name: </span>
                                                    {selectedParcel.customName || <span style={{ color: '#aaa', fontStyle: 'italic', fontWeight: 'normal' }}>None</span>}
                                                </h4>
                                                <button
                                                    onClick={() => { setRenameInput(selectedParcel.customName || ''); setIsRenaming(true); }}
                                                    style={{ background: 'none', border: 'none', color: '#0D9488', fontSize: '0.8rem', cursor: 'pointer', textDecoration: 'underline', padding: 0, fontFamily: '"Inter", sans-serif', marginTop: '2px' }}
                                                >
                                                    {selectedParcel.customName ? 'Rename' : 'Add Name'}
                                                </button>
                                            </div>
                                        )}

                                        <div style={{ fontSize: '0.85rem', color: '#475569', marginBottom: '10px' }}>
                                            Land Use: {selectedParcel.name}
                                        </div>

                                        <div style={{ display:'flex', gap:'15px', fontSize:'0.85rem', color:'#475569', marginTop: isRenaming ? '10px' : '0' }}>
                                            <span>Area: {selectedParcel.area}</span>
                                            <span>Perim: {selectedParcel.perimeter}</span>
                                        </div>
                                    </>
                                )}
                            </div>
                            <button onClick={() => { setSelectedParcel(null); setIsWholeFarmBidMode(false); }} style={{ background: 'none', border: 'none', fontSize: '1.2rem', cursor: 'pointer', color: '#475569' }}>✕</button>
                        </div>

                        {isAuctionActive ? (
                            <form onSubmit={handleSaveDraft}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                                    <label style={{ ...styles.label, ...styles.hHighlight, margin: 0 }}>Select Action</label>

                                    {isGrantScheme && currentActionDef && (
                                        <span style={{ fontSize: '0.85rem', color: '#475569', fontFamily: '"Inter", sans-serif' }}>
                                            Rate for Selection: <strong style={{color: '#0F172A'}}>£{getAdjustedGrantRate()} / {currentActionDef.unit}</strong>
                                        </span>
                                    )}
                                </div>

                                <select style={styles.select} value={selectedAction} onChange={(e) => handleActionChange(e.target.value)} required>
                                    <option value="" style={{ color: '#475569' }}>-- Choose Option --</option>
                                    {activeEventDetails && activeEventDetails.allowedActions
                                        .filter(a => isWholeFarmBidMode ? a.isWholeFarm : !a.isWholeFarm)
                                        .map(a => (
                                            <option key={a.actionId} value={a.actionId}>{a.label} ({a.unit})</option>
                                    ))}
                                </select>

                                {currentActionDef && currentActionDef.unit !== 'farm' && (isWholeFarmBidMode || currentActionDef.unit !== 'ha') && (
                                    <div>
                                        <label style={styles.label}>Quantity ({currentActionDef.unit})</label>
                                        <input
                                            type="number"
                                            step="0.01"
                                            placeholder={`Amount in ${currentActionDef.unit}`}
                                            value={bidUnits}
                                            onChange={(e) => setBidUnits(e.target.value)}
                                            style={{...styles.input, marginBottom: '15px'}}
                                            required
                                        />
                                    </div>
                                )}

                                <ActionVariationsPicker
                                    variations={currentActionVariations}
                                    selected={selectedVariations}
                                    onChange={handleVariationChange}
                                    styles={styles}
                                />

                                {!isGrantScheme ? (
                                    <>
                                        {showRatePerHaField && (
                                            <div>
                                                <label style={styles.label}>Rate (£/ha)</label>
                                                <input
                                                    type="number"
                                                    step="0.01"
                                                    placeholder="e.g. 650.00"
                                                    value={ratePerHa}
                                                    onChange={handleRatePerHaChange}
                                                    style={{...styles.input, marginBottom: '10px'}}
                                                />
                                            </div>
                                        )}
                                        {isGrantsContribution && (
                                            <>
                                                <label style={styles.label}>Total Action Cost (£)</label>
                                                <input type="number" placeholder="e.g. 5000" value={totalActionCost} onChange={(e) => setTotalActionCost(e.target.value)} style={{...styles.input, marginBottom: '15px'}} required />
                                            </>
                                        )}
                                        <label style={styles.label}>{isGrantsContribution ? 'Grant Contribution Bid (£)' : 'Your Total Price (£)'}</label>
                                        <input type="number" placeholder="e.g. 1500" value={bidAmount} onChange={(e) => setBidAmount(e.target.value)} onBlur={handleTotalPriceBlur} style={{...styles.input, marginBottom: (getUnitPriceDisplay() && !showRatePerHaField) ? '10px' : '20px'}} required />
                                        {getUnitPriceDisplay() && !showRatePerHaField && (
                                            <div style={{
                                                backgroundColor: '#F8FAFC', border: '1px solid #0D9488', color: '#0D9488',
                                                padding: '8px 12px', borderRadius: '4px', marginBottom: '20px',
                                                fontSize: '0.85rem', fontWeight: 'bold', fontFamily: '"Inter", sans-serif'
                                            }}>
                                                ℹ️ {getUnitPriceDisplay()}
                                            </div>
                                        )}
                                        {isGrantsContribution && (
                                            <>
                                                {bidAmount && totalActionCost > 0 && (
                                                    <div style={{
                                                        backgroundColor: '#F8FAFC', border: '1px solid #0D9488', color: '#0D9488',
                                                        padding: '8px 12px', borderRadius: '4px', marginBottom: '10px',
                                                        fontSize: '0.85rem', fontWeight: 'bold', fontFamily: '"Inter", sans-serif'
                                                    }}>
                                                        Contribution: {((parseFloat(bidAmount) / parseFloat(totalActionCost)) * 100).toFixed(1)}%
                                                        {currentActionDef?.maxContributionPercent !== undefined && ` (max ${currentActionDef.maxContributionPercent}%)`}
                                                    </div>
                                                )}
                                                <label style={styles.label}>Price Evidence</label>
                                                <div style={{ border: '1px dashed #CBD5E1', borderRadius: '4px', padding: '10px', marginBottom: '20px' }}>
                                                    <label style={{ ...styles.btnReversed, display: 'inline-block', cursor: 'pointer', margin: 0 }}>
                                                        {priceEvidenceFile ? priceEvidenceFile.name : '+ Add File'}
                                                        <input type="file" accept=".pdf,.png,.jpg,.jpeg,.docx" onChange={(e) => setPriceEvidenceFile(e.target.files[0])} style={{ display: 'none' }} />
                                                    </label>
                                                    <div style={{ fontSize: '0.75rem', color: '#475569', marginTop: '6px' }}>PDF, PNG, JPG, JPEG or DOCX — up to 10MB.</div>
                                                </div>
                                            </>
                                        )}
                                    </>
                                ) : (
                                    <div style={{ padding: '15px', backgroundColor: '#F0FDF4', border: '1px solid #16A34A', color: '#166534', borderRadius: '6px', marginBottom: '20px', fontSize: '0.85rem', fontFamily: '"Inter", sans-serif' }}>
                                        <strong>ℹ️ Fixed-Rate Grant:</strong> You do not need to enter a competitive price. Your payout will be calculated automatically based on the action's fixed rate and your field data. Just save your draft below!
                                    </div>
                                )}

                                <div style={{display:'flex', gap:'10px'}}>
                                    <button type="submit" style={{...styles.btnPrimary, flex: 1}}>
                                        {existingBid ? (existingBid.status === 'draft' ? 'Update Draft' : 'Revise Bid') : 'Save Draft'}
                                    </button>
                                </div>
                            </form>
                        ) : (
                            <div style={styles.alertError}><strong>Bidding Closed</strong></div>
                        )}
                    </div>
            </div>
        ) : (
            <>
                <div style={{ gridColumn: '1', gridRow: '2' }}>{draftBidsCard}</div>
                <div style={{ gridColumn: '2', gridRow: '2' }}>{submittedBidsCard}</div>
            </>
        )}
    </div>
    )}
    </div>

    {showBatchModal && (
        <div style={styles.modalOverlay}>
            <div style={{ ...styles.modalBox, maxWidth: '700px' }}>
                <h3 style={{marginTop:0, color: '#0F172A'}}>Review & Submit Bids</h3>
                <p style={{...styles.textMuted, fontSize:'0.85rem'}}>
                    {drafts.length} draft bid{drafts.length !== 1 ? 's' : ''} for this event. Remove or edit any before submitting.
                </p>

                <div style={{ maxHeight: '400px', overflowY: 'auto', margin: '15px 0' }}>
                    {draftGroups.map(group => (
                        <div key={group.actionId} style={{ marginBottom: '10px' }}>
                            <div style={styles.groupHeader}>
                                <span style={styles.groupHeaderLabel}>{group.label}</span>
                                <span style={styles.groupSubtotal}>
                                    {formatGroupQuantity(group) ? `${formatGroupQuantity(group)} · ` : ''}{formatGroupCost(group)}
                                </span>
                            </div>
                            <ul style={{listStyle:'none', padding:0, margin:0}}>
                                {group.bids.map(bid => {
                                    const displayPrice = isGrantScheme ? Math.round(bid.amount).toLocaleString('en-GB') : bid.amount.toLocaleString('en-GB');
                                    return (
                                        <li key={bid._id} style={{ padding:'10px 0', borderBottom:'1px solid #f0f0f0', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                                            <div>
                                                <div style={{ fontSize: '12px', fontWeight: 'bold', fontFamily: '"Inter", sans-serif', color:'#0D9488' }}>
                                                    {bid.parcel ? bid.parcel.parcelRef : "Whole Farm Action"}
                                                    {bid.parcel && bid.parcel.customName && (
                                                        <span style={{ color: '#0F172A', marginLeft: '6px', fontWeight:'normal' }}>— {bid.parcel.customName}</span>
                                                    )}
                                                </div>
                                                <div style={{fontSize:'0.8rem', color:'#475569', marginTop:'2px'}}>
                                                    {isGrantScheme ? 'Grant Value: ' : ''}£{displayPrice} {bid.units > 0 && bid.parcel && group.unit !== 'ha' ? `(Qty: ${bid.units})` : ''}
                                                </div>
                                            </div>
                                            <div style={{ display:'flex', gap:'5px' }}>
                                                <button onClick={() => { setShowBatchModal(false); handleEditBid(bid); }} style={{...styles.btnSecondary, fontSize:'0.75rem', padding:'6px'}}>Edit</button>
                                                <button onClick={() => handleDeleteBid(bid._id)} style={{...styles.btnDangerOutline, fontSize:'0.75rem', padding:'6px'}}>Remove</button>
                                            </div>
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                    ))}
                    {drafts.length === 0 && (
                        <p style={{...styles.textMuted, textAlign:'center', padding:'20px'}}>No drafts remaining — close this window to return to your bids.</p>
                    )}
                </div>

                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', borderTop:'1px solid #eee', paddingTop:'15px', fontWeight:'bold' }}>
                    <span>Total ({drafts.length} bid{drafts.length !== 1 ? 's' : ''})</span>
                    <span>£{(isGrantScheme ? Math.round(draftsGrandTotal) : draftsGrandTotal).toLocaleString('en-GB')}</span>
                </div>

                <div style={{backgroundColor:'#163321', padding:'15px', borderRadius:'6px', marginTop:'15px'}}>
                    <div style={{display:'flex', gap:'10px', alignItems:'start', marginBottom:'15px'}}>
                        <input type="checkbox" checked={acceptedTerms} onChange={(e) => setAcceptedTerms(e.target.checked)} style={{marginTop:'3px', cursor: 'pointer'}} />
                        <div style={{fontSize:'0.85rem', color: '#FFFFFF', fontFamily: '"Inter", sans-serif'}}>
                            I confirm accuracy & accept the {' '}
                            {activeEventDetails?.termsAndConditions ? (
                                <span onClick={() => setShowTermsModal(true)} style={{textDecoration: 'underline', cursor: 'pointer', color: '#38BDF8', fontWeight: 'bold'}}>Terms & Conditions</span>
                            ) : (
                                "Terms & Conditions"
                            )}.
                        </div>
                    </div>
                    <div style={{display:'flex', gap:'10px'}}>
                        <button onClick={() => setShowBatchModal(false)} style={{...styles.btnReversed, flex:1}}>Back</button>
                        <button
                            onClick={() => { setShowBatchModal(false); handleCommitBids(); }}
                            disabled={!acceptedTerms || drafts.length === 0}
                            style={
                                (acceptedTerms && drafts.length > 0)
                                ? { ...styles.btnPrimary, flex: 2 }
                                : { ...styles.btnPrimary, flex: 2, backgroundColor: '#475569', color: '#FFFFFF', opacity: 0.5, cursor: 'not-allowed' }
                            }
                        >
                            Confirm & Submit
                        </button>
                    </div>
                </div>
            </div>
        </div>
    )}

    {isProposalModalOpen && (
        <ProposalModal
            activeEventDetails={activeEventDetails}
            isAuctionActive={isAuctionActive}
            selectedAction={selectedAction}
            setSelectedAction={setSelectedAction}
            proposalForm={proposalForm}
            setProposalForm={setProposalForm}
            existingAttachments={existingAttachments}
            setExistingAttachments={setExistingAttachments}
            newAttachmentFiles={newAttachmentFiles}
            setNewAttachmentFiles={setNewAttachmentFiles}
            onClose={() => setIsProposalModalOpen(false)}
            onSave={handleSaveProposalDraft}
        />
    )}
    </>
    );
};

export default AuctionBiddingView;
